/**
 * AIProcurementBrief — Phase P3-8.1.
 *
 * Executive AI brief rendered at the top of the Procurement Dashboard.
 * Uses GPT-4o-mini via the Emergent Universal Key through the
 * /api/procurement/dashboard/brief backend endpoint.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import {
  Sparkles, RefreshCw, AlertTriangle, Lightbulb, CheckCircle2, Clock,
} from 'lucide-react';

import { Card, CardContent } from '../../../../components/ui/card';
import { Button } from '../../../../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AIProcurementBrief = ({ companyId, currency }) => {
  const [brief, setBrief] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  const fetchBrief = useCallback(async (force = false) => {
    try {
      if (force) setRefreshing(true); else setLoading(true);
      setError(null);
      const token = localStorage.getItem('admin_token');
      const params = new URLSearchParams();
      if (companyId) params.append('company_id', companyId);
      if (currency) params.append('currency', currency);
      if (force) params.append('force_refresh', 'true');
      const r = await axios.get(
        `${API_URL}/api/procurement/dashboard/brief?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` }, timeout: 30000 },
      );
      if (r.data?.status === 'error') {
        setError(r.data.error || 'AI brief unavailable');
        setBrief(null);
      } else {
        setBrief(r.data);
      }
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || 'Failed to fetch AI brief');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [companyId, currency]);

  useEffect(() => {
    fetchBrief(false);
  }, [fetchBrief]);

  if (loading) {
    return (
      <Card className="overflow-hidden" data-testid="ai-brief-card">
        <CardContent className="p-5">
          <div className="flex items-center gap-3 text-slate-500">
            <Sparkles className="h-5 w-5 animate-pulse text-[#FF6B35]" />
            <span className="text-sm">Generating AI procurement brief…</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error || !brief) {
    return (
      <Card data-testid="ai-brief-card">
        <CardContent className="p-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-amber-700">
              <AlertTriangle className="h-5 w-5" />
              <span className="text-sm">{error || 'Brief not available right now.'}</span>
            </div>
            <Button size="sm" variant="outline" onClick={() => fetchBrief(true)} data-testid="ai-brief-retry">
              <RefreshCw className="h-3.5 w-3.5 mr-1" />
              Retry
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  const ts = brief.generated_at ? new Date(brief.generated_at) : null;

  return (
    <Card
      className="overflow-hidden border-l-4"
      style={{ borderLeftColor: '#FF6B35' }}
      data-testid="ai-brief-card"
    >
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-4 mb-3">
          <div className="flex items-center gap-2">
            <div
              className="flex h-8 w-8 items-center justify-center rounded-md"
              style={{
                background: 'linear-gradient(135deg, #FF6B35, #F77F00)',
              }}
            >
              <Sparkles className="h-4 w-4 text-white" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-slate-800">AI Procurement Brief</h3>
              <p className="text-[11px] text-slate-500">
                {brief.model || 'AI'} ·
                {' '}
                {brief.cached ? (
                  <span className="inline-flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    Cached
                  </span>
                ) : 'Fresh'}
                {ts ? ` · ${ts.toLocaleTimeString()}` : ''}
              </p>
            </div>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={() => fetchBrief(true)}
            disabled={refreshing}
            data-testid="ai-brief-refresh"
          >
            <RefreshCw className={`h-3.5 w-3.5 mr-1 ${refreshing ? 'animate-spin' : ''}`} />
            Regenerate
          </Button>
        </div>

        <p className="text-sm text-slate-700 leading-relaxed mb-4" data-testid="ai-brief-summary">
          {brief.summary}
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {brief.highlights?.length > 0 && (
            <div data-testid="ai-brief-highlights">
              <div className="flex items-center gap-1.5 mb-2">
                <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
                <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-600">
                  Highlights
                </span>
              </div>
              <ul className="space-y-1.5">
                {brief.highlights.map((h, i) => (
                  <li key={i} className="flex items-start gap-1.5 text-xs text-slate-700">
                    <span className="mt-1 block h-1 w-1 shrink-0 rounded-full bg-emerald-500" />
                    <span>{h}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {brief.risks?.length > 0 && (
            <div data-testid="ai-brief-risks">
              <div className="flex items-center gap-1.5 mb-2">
                <AlertTriangle className="h-3.5 w-3.5 text-red-600" />
                <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-600">
                  Risks
                </span>
              </div>
              <ul className="space-y-1.5">
                {brief.risks.map((r, i) => (
                  <li key={i} className="flex items-start gap-1.5 text-xs text-slate-700">
                    <span className="mt-1 block h-1 w-1 shrink-0 rounded-full bg-red-500" />
                    <span>{r}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {brief.recommendations?.length > 0 && (
            <div data-testid="ai-brief-recommendations">
              <div className="flex items-center gap-1.5 mb-2">
                <Lightbulb className="h-3.5 w-3.5 text-amber-600" />
                <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-600">
                  Recommendations
                </span>
              </div>
              <ul className="space-y-1.5">
                {brief.recommendations.map((r, i) => (
                  <li key={i} className="flex items-start gap-1.5 text-xs text-slate-700">
                    <span className="mt-1 block h-1 w-1 shrink-0 rounded-full bg-amber-500" />
                    <span>{r}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default AIProcurementBrief;
